import { Link } from '@tanstack/react-router';
import {
  CircleArrowLeft,
  Download,
  Loader2,
  Search,
} from 'lucide-react';
import { useMemo, useState } from 'react';

import {
  downloadFailedPaymentsCSVAPI,
  useFetchFailedPayments,
} from '~entities/payment/api/failed-payments';

import { useGetCurrency } from '~shared/api/currency-api';
import { InputDatePicker } from '~shared/components/input-date-picker';
import { useStudioDateFormatter } from '~shared/hooks/useStudioDateFormatter';
import { Button } from '~shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';
import { Input } from '~shared/shadcn/ui/input';

import { FailedPaymentsTable } from './failed-payments-table';

const toStartOfDayISOString = (dateString: string): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  date.setUTCHours(0, 0, 0, 0);
  return date.toISOString();
};

const toEndOfDayISOString = (dateString: string): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  date.setUTCHours(23, 59, 59, 999);
  return date.toISOString();
};

export const FailedPaymentsPage = () => {
  const { formatInStudioTz } = useStudioDateFormatter();
  const [startDate, setStartDate] = useState<string>(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0] ?? '';
  });
  const [endDate, setEndDate] = useState<string>(() => {
    return new Date().toISOString().split('T')[0] ?? '';
  });
  const [searchStartDate, setSearchStartDate] = useState<string>(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0] ?? '';
  });
  const [searchEndDate, setSearchEndDate] = useState<string>(() => {
    return new Date().toISOString().split('T')[0] ?? '';
  });
  const [page, setPage] = useState(1);
  const limit = 20;
  const [hasSearched, setHasSearched] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');

  const isDateRangeInvalid = useMemo(() => {
    if (!startDate || !endDate) {
      return false;
    }

    return new Date(startDate) > new Date(endDate);
  }, [startDate, endDate]);

  const apiStartDate = useMemo(
    () => (searchStartDate ? toStartOfDayISOString(searchStartDate) : ''),
    [searchStartDate],
  );

  const apiEndDate = useMemo(
    () => (searchEndDate ? toEndOfDayISOString(searchEndDate) : ''),
    [searchEndDate],
  );

  // Get currency data
  const { data: currencyData } = useGetCurrency();

  const { data, isLoading, error, refetch } = useFetchFailedPayments(
    {
      startDate: apiStartDate,
      endDate: apiEndDate,
      page,
      limit,
      search: appliedSearchTerm || undefined,
    },
    { enabled: hasSearched && !!apiStartDate && !!apiEndDate },
  );

  const handleSearch = () => {
    if (!startDate || !endDate || isDateRangeInvalid) {
      return;
    }

    setSearchStartDate(startDate);
    setSearchEndDate(endDate);
    setAppliedSearchTerm(searchTerm.trim());
    setPage(1);
    setHasSearched(true);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1) {
      setPage(newPage);
    }
  };

  const handleDownloadCSV = async () => {
    if (!hasSearched || !apiStartDate || !apiEndDate) return;

    try {
      const blob = await downloadFailedPaymentsCSVAPI({
        startDate: apiStartDate,
        endDate: apiEndDate,
        page: 1,
        limit: 10,
        search: appliedSearchTerm || undefined,
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `failed-payments-${searchStartDate}-to-${searchEndDate}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading CSV:', error);
    }
  };

  // Memoized currency formatter to avoid recreating on every render
  const formatCurrency = useMemo(() => {
    const currency = currencyData?.name ?? 'USD';
    const symbol = currencyData?.symbol ?? '$';

    return (amount: number) => {
      // If we have a symbol, use it directly for better performance
      if (symbol && symbol !== currency) {
        return `${symbol}${amount.toFixed(2)}`;
      }

      // Fallback to Intl.NumberFormat for proper currency formatting
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
      }).format(amount);
    };
  }, [currencyData?.name, currencyData?.symbol]);

  const formatDate = (dateString: string) => {
    if (dateString === 'N/A' || !dateString) {
      return 'N/A';
    }
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
    } catch {
      return 'N/A';
    }
  };

  const formatDateTime = (dateString: string) => {
    if (dateString === 'N/A' || !dateString) {
      return 'N/A';
    }
    try {
      return formatInStudioTz(dateString, 'MM/dd/yyyy hh:mm a');
    } catch {
      return 'N/A';
    }
  };

  if (isLoading) {
    return (
      <div className='flex min-h-[400px] items-center justify-center'>
        <Loader2 className='size-8 animate-spin' />
      </div>
    );
  }

  if (error) {
    return (
      <div className='space-y-4'>
        <div className='flex items-center gap-4'>
          <Link to='/dashboard' className='flex items-center gap-2'>
            <CircleArrowLeft className='size-6' />
            <h2 className='text-2xl font-semibold'>Failed Payments</h2>
          </Link>
        </div>
        <div className='mb-6 mt-8 border-t' />
        <Card>
          <CardContent className='p-6'>
            <div className='text-center text-red-600'>
              Error loading failed payments. Please try again.
            </div>
            <div className='mt-4 text-center'>
              <Button onClick={() => refetch()}>Reload</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <div className='flex items-center gap-4'>
        <Link to='/dashboard' className='flex items-center gap-2'>
          <CircleArrowLeft className='size-6' />
          <h2 className='text-2xl font-semibold'>Failed Payments</h2>
        </Link>
      </div>
      <div className='mb-6 mt-8 border-t' />

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className='flex items-center gap-2'>
            <Search className='size-5' />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className='flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center'>
            <div className='flex min-w-0 items-center gap-2'>
              <InputDatePicker
                id='startDate'
                value={startDate}
                onChange={setStartDate}
                ariaLabel='Start date'
                align='start'
                displayFormat='MM/dd/yyyy'
                className='h-8 min-w-0 flex-1 px-2 sm:w-40 sm:flex-none'
              />
              <span className='shrink-0 text-sm text-muted-foreground'>to</span>
              <InputDatePicker
                id='endDate'
                value={endDate}
                onChange={setEndDate}
                ariaLabel='End date'
                align='end'
                displayFormat='MM/dd/yyyy'
                className='h-8 min-w-0 flex-1 px-2 sm:w-40 sm:flex-none'
              />
            </div>
            <div className='relative w-full max-w-xs'>
              <Search className='pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground' />
              <Input
                type='search'
                placeholder='Search name or email'
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                }}
                className='pl-9'
                aria-label='Search by name or email'
              />
            </div>
            <Button
              onClick={handleSearch}
              variant='default'
              className='flex items-center gap-2'
              disabled={!startDate || !endDate || isDateRangeInvalid}
            >
              <Search className='size-4' />
              Search
            </Button>
            {hasSearched && (
              <>
                <Button onClick={() => refetch()} variant='outline'>
                  Refresh
                </Button>
                <Button
                  onClick={handleDownloadCSV}
                  variant='outline'
                  className='flex items-center gap-2'
                  disabled={!data || data.data.length === 0}
                >
                  <Download className='size-4' />
                  Download CSV
                </Button>
              </>
            )}
          </div>
          {isDateRangeInvalid && (
            <p className='mt-2 text-sm text-red-600'>
              Start date must be on or before end date.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Summary */}
      {!hasSearched ? (
        <Card>
          <CardContent className='p-8'>
            <div className='text-center text-gray-500'>
              <Search className='mx-auto mb-4 size-12 text-gray-300' />
              <h3 className='mb-2 text-lg font-medium'>
                Search Failed Payments
              </h3>
              <p>
                Select a date range and click &quot;Search&quot; to view failed
                payments.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        data && (
          <div className='flex flex-col gap-4 md:grid md:grid-cols-4'>
            <Card>
              <CardContent className='p-4'>
                <div className='text-sm font-medium text-gray-600'>
                  Total Failed Payments
                </div>
                <div className='text-2xl font-bold'>
                  {data.pagination.total}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className='p-4'>
                <div className='text-sm font-medium text-gray-600'>
                  Total Failed Amount
                </div>
                <div className='text-2xl font-bold text-red-600'>
                  {formatCurrency(
                    data.data.reduce(
                      (sum, payment) => sum + payment.finalAmount,
                      0,
                    ),
                  )}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className='p-4'>
                <div className='text-sm font-medium text-gray-600'>Page</div>
                <div className='text-2xl font-bold'>
                  {data.pagination.page} of{' '}
                  {Math.ceil(data.pagination.total / data.pagination.limit)}
                </div>
              </CardContent>
            </Card>
          </div>
        )
      )}

      {/* Payments Table */}
      {hasSearched && (
        <Card>
          <CardHeader>
            <CardTitle>Failed Payments</CardTitle>
          </CardHeader>
          <CardContent>
            {data && data.data.length > 0 ? (
              <div className='space-y-4'>
                <FailedPaymentsTable
                  payments={data.data}
                  formatCurrency={formatCurrency}
                  formatDate={formatDate}
                  formatDateTime={formatDateTime}
                />

                {/* Pagination */}
                {data.pagination.total > 0 && (
                  <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
                    <div className='text-sm text-gray-600'>
                      Showing{' '}
                      {(data.pagination.page - 1) * data.pagination.limit + 1}{' '}
                      to{' '}
                      {Math.min(
                        data.pagination.page * data.pagination.limit,
                        data.pagination.total,
                      )}{' '}
                      of {data.pagination.total} results
                    </div>
                    <div className='flex items-center justify-between gap-3 sm:justify-end'>
                      <span className='text-sm text-gray-600'>
                        Page {data.pagination.page} of{' '}
                        {Math.max(
                          1,
                          Math.ceil(
                            data.pagination.total / data.pagination.limit,
                          ),
                        )}
                      </span>
                      <div className='flex items-center gap-2'>
                        <Button
                          variant='outline'
                          size='sm'
                          onClick={() => {
                            handlePageChange(page - 1);
                          }}
                          disabled={page <= 1}
                        >
                          Previous
                        </Button>
                        <Button
                          variant='outline'
                          size='sm'
                          onClick={() => {
                            handlePageChange(page + 1);
                          }}
                          disabled={
                            !data.pagination.hasMore ||
                            page >=
                              Math.ceil(
                                data.pagination.total / data.pagination.limit,
                              )
                          }
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className='py-8 text-center text-gray-500'>
                No failed payments found for the selected date range.
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
